import React, { useState, useEffect, useMemo } from 'react'
import { Calendar, Clock, Award, Users, Loader2 } from 'lucide-react'
import { getAllMonumentaEmployees } from '@/lib/real-data-service'

export function UpcomingEvents() {
  const [employees, setEmployees] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Load employees data
  useEffect(() => {
    const loadEmployees = async () => {
      try {
        setIsLoading(true)
        setError(null)
        const employeesData = await getAllMonumentaEmployees()
        setEmployees(employeesData)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load employees')
        console.error('Error loading employees for UpcomingEvents:', err)
      } finally {
        setIsLoading(false)
      }
    }

    loadEmployees()
  }, [])

  // Generate events from real employee data
  const events = useMemo(() => {
    if (!employees.length) return []

    const eventTypes = [
      { type: 'birthday', icon: Calendar, prefix: 'Aniversário - ' },
      { type: 'vacation', icon: Clock, prefix: 'Férias - ' },
      { type: 'training', icon: Award, prefix: 'Treinamento - ' },
      { type: 'meeting', icon: Users, prefix: 'Reunião 1:1 - ' }
    ]

    return eventTypes.map((eventType, index) => {
      const randomEmployee = employees[Math.floor(Math.random() * employees.length)]
      const futureDate = new Date()
      futureDate.setDate(futureDate.getDate() + index * 3 + 1) // Spread events over coming days

      return {
        id: index + 1,
        title: eventType.prefix + randomEmployee.name.split(' ')[0],
        date: futureDate.toISOString().split('T')[0],
        type: eventType.type,
        icon: eventType.icon
      }
    })
  }, [employees])

  return (
    <div className="card">
      <h3 className="font-display text-base sm:text-lg font-semibold text-white mb-6 bg-gradient-to-r from-white to-brand-gray-light bg-clip-text text-transparent">
        Próximos Eventos
      </h3>
      <div className="space-y-3">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 text-brand-primary animate-spin mr-3" />
            <span className="text-brand-gray text-sm font-body">Carregando eventos...</span>
          </div>
        ) : error ? (
          <div className="text-center py-8">
            <Calendar className="w-8 h-8 text-red-400 mx-auto mb-2" />
            <p className="text-red-400 text-sm font-body">Erro ao carregar eventos</p>
            <p className="text-brand-gray text-xs font-body mt-1">{error}</p>
          </div>
        ) : events.length > 0 ? (
          events.map((event) => {
            const Icon = event.icon
            return (
              <div key={event.id} className="card-interactive group flex items-center p-4 transition-all duration-200">
                <div className="p-2 rounded-monumenta bg-brand-primary/10 group-hover:bg-brand-primary/20 transition-colors duration-200 mr-4">
                  <Icon className="text-brand-primary flex-shrink-0 group-hover:scale-110 transition-transform duration-200" size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-display font-medium text-white truncate group-hover:text-brand-primary transition-colors duration-200">{event.title}</p>
                  <p className="text-xs text-brand-gray-light font-body mt-1">
                    {new Date(event.date).toLocaleDateString('pt-BR')}
                  </p>
                </div>
              </div>
            )
          })
        ) : (
          <div className="text-center py-8">
            <Calendar className="w-8 h-8 text-brand-gray mx-auto mb-2" />
            <p className="text-brand-gray text-sm font-body">Nenhum evento encontrado</p>
          </div>
        )}
      </div>
    </div>
  )
}